import type { ReactNode } from 'react';

/**
 * Shared furniture for the in-app manuals (/supplier/guide, /admin/guide).
 *
 * These are deliberately plain: a manual that depends on the app's form and
 * table components would break every time those change, and a broken manual
 * is worse than none because nobody can tell which half is out of date.
 *
 * The internal team guide is NOT built from these. It stays a static file at
 * public/guide.html so it can be opened by somebody who has not signed in
 * yet — a new starter reading it before their invite lands is the main way it
 * gets used. That difference is the reason /admin/guide could not be a static
 * file too: a page in public/ is served by the CDN before React or any
 * session exists, so there is nothing there to gate it with.
 */

export function GuidePage({
  eyebrow,
  title,
  intro,
  children,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  children: ReactNode;
}) {
  return (
    <article className="guide">
      <header className="guide-hero">
        <p className="guide-eyebrow">{eyebrow}</p>
        <h1 className="guide-title">{title}</h1>
        <p className="guide-intro">{intro}</p>
      </header>
      {children}
    </article>
  );
}

/** The jump list. Entries must match the ids passed to GuideSection. */
export function GuideToc({ entries }: { entries: { id: string; label: string }[] }) {
  return (
    <nav className="guide-toc" aria-label="In this guide">
      <p className="guide-toc-title">In this guide</p>
      <ol>
        {entries.map((entry) => (
          <li key={entry.id}>
            <a href={`#${entry.id}`}>{entry.label}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function GuideSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="guide-section" aria-labelledby={`${id}-heading`}>
      <h2 className="guide-h2" id={`${id}-heading`}>
        <span id={id} className="guide-anchor" />
        {title}
      </h2>
      {children}
    </section>
  );
}

export function GuideSubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="guide-sub">
      <h3 className="guide-h3">{title}</h3>
      {children}
    </div>
  );
}

/**
 * A numbered walk-through. Numbering is rendered rather than typed so a step
 * inserted in the middle does not silently leave two step 4s.
 */
export function GuideSteps({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className="guide-steps">
      {steps.map((step, index) => (
        // The list is a fixed, authored array — nothing reorders or filters
        // it, so the index is a stable identity here.
        <li key={index} className="guide-step">
          <span className="guide-step-n" aria-hidden="true">
            {index + 1}
          </span>
          <div className="guide-step-body">{step}</div>
        </li>
      ))}
    </ol>
  );
}

export function Callout({
  tone = 'note',
  children,
}: {
  tone?: 'note' | 'warn' | 'stop';
  children: ReactNode;
}) {
  const word = tone === 'warn' ? 'Careful' : tone === 'stop' ? 'Never' : 'Note';
  return (
    <aside className={`guide-callout guide-callout--${tone}`}>
      <span className="guide-callout-tag">{word}</span>
      <div>{children}</div>
    </aside>
  );
}

/** An inline reference to something the reader will literally see on screen. */
export function Ui({ children }: { children: ReactNode }) {
  return <span className="guide-ui">{children}</span>;
}

/** A two-column reference table. Headers are required; rows may be any node. */
export function GuideTable({
  caption,
  headers,
  rows,
}: {
  caption?: string;
  headers: string[];
  rows: ReactNode[][];
}) {
  return (
    <div className="guide-table-wrap">
      <table className="guide-table">
        {caption === undefined ? null : <caption>{caption}</caption>}
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Authored rows, fixed order.
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A labelled diagram, drawn as inline SVG.
 *
 * Screenshots were the obvious choice and are the wrong one here: they are
 * binaries in the repo that nothing type-checks, they go stale the first time
 * a button moves, and a stale screenshot is read as current far longer than
 * stale prose is. A drawn diagram shows the SHAPE of a flow, which is what a
 * reader actually needs, and it survives a restyle.
 */
export function GuideFlow({ label, steps }: { label: string; steps: string[] }) {
  const boxWidth = 150;
  const gap = 34;
  const height = 86;
  const width = steps.length * boxWidth + (steps.length - 1) * gap;
  return (
    <figure className="guide-figure">
      <svg
        className="guide-flow"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${label}: ${steps.join(', then ')}`}
      >
        {steps.map((step, index) => {
          const x = index * (boxWidth + gap);
          return (
            <g key={step}>
              <rect x={x} y={18} width={boxWidth} height={50} rx={10} className="guide-flow-box" />
              <text x={x + boxWidth / 2} y={48} className="guide-flow-text">
                {step}
              </text>
              {index < steps.length - 1 ? (
                <path
                  d={`M ${x + boxWidth + 6} 43 L ${x + boxWidth + gap - 6} 43`}
                  className="guide-flow-arrow"
                  markerEnd="url(#guide-arrowhead)"
                />
              ) : null}
            </g>
          );
        })}
        <defs>
          <marker
            id="guide-arrowhead"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="4"
            orient="auto"
          >
            <path d="M 0 1 L 6 4 L 0 7 z" className="guide-flow-head" />
          </marker>
        </defs>
      </svg>
      <figcaption>{label}</figcaption>
    </figure>
  );
}
